import { buildListingHoverCard, escapeHtml, fillListingHoverCommute, listingHoverImageUrl } from './listingHoverCard.js';
import { fetchFullListingDetails, getListingKey } from './listingDetailsApi.js';
import './listingMiniCard.css';

// Touch screens: tapping a home on the map opens a small card for it (the listing hover card with a close
// button) next to the marker, in kartvy and listvy. Tapping the card opens the full listing; the close
// button, a tap anywhere else on the map (which then does nothing else), or moving or zooming the map
// closes it. A tap on another home switches to that home's card. With a mouse, markers keep their hover card
// and a click opens the listing at once.

const GAP_PX = 22;
const MARGIN_PX = 8;
// Photos to swipe through on the card. Kartvy's listings carry one photo, so the rest come from the
// listing's details (cached per listing).
const MAX_PHOTOS = 6;
const photoCache = new Map();

const smallPhoto = (url) => String(url || '').replace(/_\d+x0\.(jpe?g|png|webp)$/i, '_640x0.$1');
const listingPhotos = (listing) => {
    const urls = [listingHoverImageUrl(listing)];
    (Array.isArray(listing?.images) ? listing.images : []).forEach((url) => {
        const small = smallPhoto(url);
        if (small && !urls.includes(small) && urls.length < MAX_PHOTOS) urls.push(small);
    });
    return urls;
};

// listingAt(pointerEvent) → the listing under a pointer on the map, or null. cardFor(listing) → { score, swedish }.
// onOpen(listing, markerEvent) opens the full listing; onHide() runs whenever the card closes (the view
// resets the marker a touch left looking hovered). Returns { handleMarkerTap(listing, markerEvent) → true
// when the tap was a touch and the card took it, hide(), detach() }.
// placement: 'marker' (next to the tapped marker) or 'bottom' (centred at the bottom of the map, kartvy).
export const attachListingMiniCard = (map, { listingAt, cardFor, onOpen, onHide, placement = 'marker' }) => {
    const container = map.getContainer();
    let lastPointerWasTouch = false;
    let current = null; // { listing, event }
    let dismissedByTap = false;

    const card = document.createElement('div');
    card.className = 'area-card listing-mini-card';
    card.setAttribute('role', 'dialog');
    document.body.appendChild(card);

    const hide = () => {
        if (!current) return;
        current = null;
        card.classList.remove('is-visible');
        onHide?.();
    };

    // The photo strip in place of the card's single photo: swipe, dots show where you are.
    const renderStrip = (urls, swedish) => {
        const image = card.querySelector('.listing-hover-image');
        if (!image) return;
        const strip = document.createElement('div');
        strip.className = 'listing-mini-strip';
        strip.innerHTML = `<div class="listing-mini-strip-track">${urls.map((url, index) => (
            `<div class="listing-mini-strip-slide"><img src="${escapeHtml(url)}" alt="" ${index ? 'loading="lazy"' : ''} draggable="false"/></div>`
        )).join('')}</div>`
            + (urls.length > 1 ? `<div class="listing-mini-strip-dots" aria-hidden="true">${urls.map((_, index) => `<i${index ? '' : ' class="is-active"'}></i>`).join('')}</div>` : '');
        strip.setAttribute('aria-label', swedish ? 'Bilder' : 'Photos');
        image.replaceWith(strip);
        const track = strip.querySelector('.listing-mini-strip-track');
        track.addEventListener('scroll', () => {
            const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
            strip.querySelectorAll('.listing-mini-strip-dots i').forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
        }, { passive: true });
    };
    const addPhotos = (listing, swedish) => {
        const key = getListingKey(listing);
        const fill = (urls) => {
            if (current?.listing !== listing || urls.length <= 1) return;
            const track = card.querySelector('.listing-mini-strip-track');
            const shown = track ? track.children.length : 1;
            if (urls.length <= shown) return;
            const scroll = track?.scrollLeft || 0;
            // Re-draw the strip with the extra photos, keeping the photo on show.
            const strip = card.querySelector('.listing-mini-strip');
            if (strip) {
                const placeholder = document.createElement('img');
                placeholder.className = 'listing-hover-image';
                strip.replaceWith(placeholder);
            }
            renderStrip(urls, swedish);
            const nextTrack = card.querySelector('.listing-mini-strip-track');
            if (nextTrack) nextTrack.scrollLeft = scroll;
        };
        if (photoCache.has(key)) {
            fill(photoCache.get(key));
            return;
        }
        if (!key || (Array.isArray(listing.images) && listing.images.length >= MAX_PHOTOS)) return;
        fetchFullListingDetails(key)
            .then((details) => {
                const urls = listingPhotos({ ...listing, images: details?.images || [] });
                photoCache.set(key, urls);
                fill(urls);
            })
            .catch(() => {});
    };

    const place = (point) => {
        const width = card.offsetWidth;
        const height = card.offsetHeight;
        if (placement === 'bottom') {
            const box = container.getBoundingClientRect();
            const bottom = Math.min(box.bottom, window.innerHeight);
            card.style.left = `${Math.max(MARGIN_PX, box.left + ((box.width - width) / 2))}px`;
            card.style.top = `${Math.max(MARGIN_PX, bottom - height - 12)}px`;
            return;
        }
        const left = Math.min(Math.max(MARGIN_PX, point.x - (width / 2)), window.innerWidth - width - MARGIN_PX);
        // Clear of the search field and buttons along the map's top edge.
        const mapTop = container.getBoundingClientRect().top;
        const minTop = Math.max(MARGIN_PX, mapTop + (window.innerWidth <= 700 ? 64 : 8));
        // Above the marker; below it when there is no room above.
        let top = point.y - height - GAP_PX;
        if (top < minTop) top = Math.min(point.y + GAP_PX, window.innerHeight - height - MARGIN_PX);
        card.style.left = `${left}px`;
        card.style.top = `${Math.max(MARGIN_PX, top)}px`;
    };

    const markerPoint = (event) => {
        const target = event?.target;
        if (target && typeof target.getLatLng === 'function') {
            const rect = container.getBoundingClientRect();
            const point = map.latLngToContainerPoint(target.getLatLng());
            return { x: rect.left + point.x, y: rect.top + point.y };
        }
        const original = event?.originalEvent;
        return original ? { x: original.clientX, y: original.clientY } : null;
    };

    const show = (listing, event) => {
        const { score, swedish } = cardFor(listing);
        const closeLabel = swedish ? 'Stäng' : 'Close';
        card.innerHTML = `${buildListingHoverCard(listing, { score, swedish })}`
            + `<button type="button" class="listing-mini-card-close" aria-label="${escapeHtml(closeLabel)}">`
            + '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>'
            + '</button>';
        card.setAttribute('aria-label', listing.address || '');
        fillListingHoverCommute(card, listing, swedish);
        current = { listing, event };
        renderStrip(listingPhotos(listing), swedish);
        addPhotos(listing, swedish);
        card.classList.add('is-visible');
        const point = markerPoint(event);
        if (point || placement === 'bottom') place(point);
    };

    card.addEventListener('click', (event) => {
        if (!current) return;
        if (event.target.closest('.listing-mini-card-close')) {
            hide();
            return;
        }
        const { listing, event: markerEvent } = current;
        hide();
        onOpen(listing, markerEvent);
    });

    // A tap outside the card closes it; on the map that tap does nothing else, unless it's on another home.
    const onDocumentPointerDown = (event) => {
        lastPointerWasTouch = event.pointerType === 'touch';
        if (!current || card.contains(event.target)) return;
        dismissedByTap = container.contains(event.target) && !listingAt(event);
        hide();
    };
    const onMapClickCapture = (event) => {
        if (!dismissedByTap) return;
        dismissedByTap = false;
        event.preventDefault();
        event.stopImmediatePropagation();
    };
    const onKeyDown = (event) => {
        if (event.key === 'Escape') hide();
    };
    // Scrolling the page closes it; swiping its own photos doesn't.
    const onScroll = (event) => {
        if (event.target instanceof Node && card.contains(event.target)) return;
        hide();
    };

    document.addEventListener('pointerdown', onDocumentPointerDown, true);
    container.addEventListener('click', onMapClickCapture, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', hide);
    document.addEventListener('keydown', onKeyDown);
    map.on('movestart zoomstart', hide);

    return {
        handleMarkerTap: (listing, event) => {
            if (!lastPointerWasTouch) return false;
            show(listing, event);
            return true;
        },
        hide,
        detach: () => {
            hide();
            card.remove();
            document.removeEventListener('pointerdown', onDocumentPointerDown, true);
            container.removeEventListener('click', onMapClickCapture, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', hide);
            document.removeEventListener('keydown', onKeyDown);
            map.off('movestart zoomstart', hide);
        },
    };
};
